import { Component, FocusEvent, ReactNode } from 'react';
import { ObjectWithId } from '@synergy/models';
import debounce from 'lodash.debounce';

import { BaseAutocompleteSuggestionType, PoolAutocompleteList } from '../autocomplete-list/pool-autocomplete-list';

export interface PoolFormAutocompleteListProps<T, PT extends BaseAutocompleteSuggestionType> {
    id?: string;
    items?: T[];
    value?: PT;
    required?: boolean;
    error?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    footer?: ReactNode;
    label?: ReactNode;
    name?: string;
    clearInputOnSelect?: boolean;
    initialValue?: string;
    disablePreventOverflow?: boolean;
    adornment?: ReactNode;
    inProgress?: boolean;
    inputRef?: (ref: HTMLInputElement) => void;
    onSelected?: (value: T | null) => void;
    onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
    onValueChanged?: (value: string) => void;
}

type FunctionOrNode = (() => ReactNode) | ReactNode;

interface PoolFormAutocompleteListState<T, PT> {
    suggestionData: PT[];
    items?: T[];
}

// Generics needed for common interface with abstract base class
/* eslint-disable @typescript-eslint/no-unused-vars */
export interface PoolFormAutocompleteList<
    T extends ObjectWithId,
    PT extends BaseAutocompleteSuggestionType,
    P = Record<string, unknown>
> {
    handleValueChanged?(value: string): void;
    getDisplayValue?(suggestion: BaseAutocompleteSuggestionType): string;
}
/* eslint-enable */

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export abstract class PoolFormAutocompleteList<
        T extends ObjectWithId,
        PT extends BaseAutocompleteSuggestionType,
        P = Record<string, unknown>
    >
    extends Component<P & PoolFormAutocompleteListProps<T, PT>, PoolFormAutocompleteListState<T, PT>>
    implements PoolFormAutocompleteList<T, PT, P>
{
    public footer?: FunctionOrNode = undefined;
    public label?: FunctionOrNode = undefined;
    public resetSearch?: () => void = undefined;
    public showAdornment?: (value: PT | undefined) => boolean = undefined;
    public abstract getSuggestionValue: (value: PT) => string;
    public abstract getPartial: (items: T[]) => PT[];
    public hideId = false;
    public adornment?: ReactNode;
    public alwaysShowSuggestions = true;

    public override state: PoolFormAutocompleteListState<T, PT> = {
        suggestionData: [],
    };

    constructor(props: P & PoolFormAutocompleteListProps<T, PT>) {
        super(props);
        this.handleSelected = this.handleSelected.bind(this);
        if (this.handleValueChanged) {
            this.handleValueChanged = debounce(this.handleValueChanged.bind(this), 800);
        }
    }

    public handleSelected(value: BaseAutocompleteSuggestionType | null) {
        if (this.props.onSelected) {
            if (value) {
                const id = typeof value.id === 'number' ? value.id : parseInt(value.id as string, 10);
                const found = this.getItems().find(item => item.id === id);
                this.props.onSelected(found || null);
            } else {
                this.props.onSelected(null);
            }
        }
        if (this.resetSearch) {
            this.resetSearch();
        }
    }

    private getNode(node?: FunctionOrNode): ReactNode | undefined {
        if (!node) {
            return undefined;
        }
        if (node instanceof Function) {
            return node();
        }
        return node;
    }

    // Prefer items from local state - if undefined take from props
    public getItems = (props?: PoolFormAutocompleteListProps<T, PT>, state?: PoolFormAutocompleteListState<T, PT>) => {
        return (state || this.state).items || (props || this.props).items || [];
    };

    public override componentDidMount() {
        this.setState({ suggestionData: this.getPartial(this.props.items ?? []) });
    }

    public componentWillUpdate(
        nextProps: P & PoolFormAutocompleteListProps<T, PT>,
        nextState: PoolFormAutocompleteListState<T, PT>
    ) {
        if (nextProps.items !== this.props.items || nextState.items !== this.state.items) {
            this.setState({ suggestionData: this.getPartial(this.getItems(nextProps, nextState)) });
        }
    }

    public override render() {
        const {
            id,
            value,
            required,
            error,
            disabled,
            readOnly,
            initialValue,
            clearInputOnSelect,
            disablePreventOverflow,
            inputRef,
            footer,
            label,
            onValueChanged,
            adornment,
            inProgress,
        } = this.props;

        return (
            <PoolAutocompleteList
                id={id || ''}
                initialSuggestionValue={value}
                data={this.state.suggestionData}
                getSuggestionValue={suggestion => this.getSuggestionValue(suggestion as PT)}
                handleSuggestionSelected={this.handleSelected}
                footer={footer || this.getNode(this.footer)}
                label={label || this.getNode(this.label)}
                onValueChanged={onValueChanged || this.handleValueChanged}
                // Passed through
                getDisplayValue={this.getDisplayValue}
                required={required}
                error={error}
                disabled={disabled}
                readOnly={readOnly}
                initialValue={initialValue}
                clearInputOnSelect={clearInputOnSelect}
                inputRef={inputRef}
                disablePreventOverflow={disablePreventOverflow}
                exclude={(this.hideId && ['id']) || undefined}
                adornment={adornment || (this.showAdornment && this.showAdornment(value) && this.adornment)}
                alwaysShowSuggestions={this.alwaysShowSuggestions}
                showProgress={inProgress}
            />
        );
    }
}
